import { observer } from "mobx-react"
import { useMemo } from "react"
import { TextField, Typography, Box } from "@mui/material"

import { getSectionFieldName } from "src/components/ConfigurableDynamicFormFields/helpers/fieldNameHelpers"
import { ConfigurableDynamicFormFieldsStore } from "src/components/ConfigurableDynamicFormFields/store"
import { useStore } from "src/store/lib/useStore"
import { IConfigurableDynamicFormFieldsProps } from "src/components/ConfigurableDynamicFormFields/types/form-field"

export const ConfigurableDynamicFormFieldsAutoUuid = observer(
    ({ sectionName, field }: IConfigurableDynamicFormFieldsProps) => {
        const store = useStore(ConfigurableDynamicFormFieldsStore)

        const formFieldKey = useMemo(
            () => getSectionFieldName(sectionName, field.name),
            [field.name, sectionName],
        )

        const value = useMemo(
            () => (store.form.get(formFieldKey) as string) ?? "",
            [store.form, formFieldKey],
        )

        // If there's no value (creating new config), show a message instead of input field
        if (value === "") {
            return (
                <Box
                    sx={{
                        p: 2,
                        backgroundColor: "grey.50",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "grey.300",
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        UUID will be automatically generated when this
                        configuration is created
                    </Typography>
                </Box>
            )
        }

        return (
            <TextField
                label={field.label}
                value={value}
                fullWidth
                InputProps={{
                    readOnly: true,
                }}
                variant="outlined"
                helperText={field.description}
                sx={{
                    "& .MuiInputBase-input": {
                        fontFamily: "monospace",
                        fontSize: "0.875rem",
                    },
                }}
            />
        )
    },
)
